import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../../auth/useAuth";
import { CompanyRole } from "../../../../auth/AuthProvider";
import { PageTabs } from "../../../../components/PageTabs";
import { useActiveTabs } from "../../../../utils/useActiveTabs";
import {
  AppSettingsTabs,
  AppSettingsTabsView,
  TOnboardingConfig,
} from "../../models";
import { useAppSettings } from "../../hooks/useAppSettings";
import { useAppSettingsUpdate } from "../../hooks/useAppSettingsUpdate";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { useAppSettingsSchema } from "../../hooks/useAppSettingsSchema";
import { AppSettingsSchemaProvider } from "../AppSettingsSchema/context";
import { GeneralTab } from "../GeneralTab/index";
import { AdvancedTab } from "../AdvancedTab/index";
import { TemplatesTab } from "../TemplatesTab/index";
import { QuestionnaireTab } from "../QuestionnaireTab/index";

const tabList = [
  { id: 0, label: "General", url: `${AppSettingsTabs.GENERAL}` },
  { id: 1, label: "Questionnaire", url: `${AppSettingsTabs.QUESTIONNAIRE}` },
  { id: 2, label: "Templates", url: `${AppSettingsTabs.TEMPLATES}` },
  { id: 3, label: "Advanced", url: `${AppSettingsTabs.ADVANCED}` },
];

export const AppSettings: FC<{ appId: string; publicKey: string }> = memo(
  ({ appId, publicKey }) => {
    const { activeTab, handleChange } = useActiveTabs();
    const { search, pathname } = useLocation();
    const { companyRole, isSuperAdmin } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [data, , dataError] = useAppSettings(appId);
    const [updateResult, updateLoading, updateError, update] =
      useAppSettingsUpdate();
    const [preparedSchema, schemaLoading, schemaError] = useAppSettingsSchema();
    const [config, setConfig] = useState<TOnboardingConfig>({});
    const [persistedSerializedConfig, setPersistedSerializedConfig] = useState<
      string | null
    >(null);

    useEffect(() => {
      const searchIndex = AppSettingsTabsView.findIndex((tab) => tab === search);
      const nextTab = searchIndex >= 0 ? searchIndex : 0;
      const nextUrl =
        searchIndex >= 0 ? `${pathname}${search}` : `${pathname}?general`;

      if (activeTab !== nextTab || searchIndex < 0) {
        handleChange(null, nextTab, nextUrl);
      }
    }, [activeTab, handleChange, pathname, search]);

    useEffect(() => {
      const persistedSettings = updateResult || data;
      if (!persistedSettings) {
        return;
      }

      const raw = persistedSettings.onboardingConfig || "{}";
      try {
        const parsed = JSON.parse(raw) as TOnboardingConfig;
        const serialized = JSON.stringify(parsed, null, 2);
        setConfig(parsed);
        setPersistedSerializedConfig(serialized);
      } catch {
        // ignore invalid payload shape to avoid clobbering current edits
      }
    }, [data, updateResult]);

    useEffect(() => {
      if (updateResult) {
        enqueueSnackbar("App settings updated", { variant: "success" });
      }
    }, [enqueueSnackbar, updateResult]);

    const onConfigChange = useCallback(
      (updater: (prev: TOnboardingConfig) => TOnboardingConfig) => {
        setConfig((prev) => updater(prev));
      },
      [],
    );

    const canSave = companyRole === CompanyRole.ADMIN || isSuperAdmin;
    const currentSerializedConfig = useMemo(
      () => JSON.stringify(config, null, 2),
      [config],
    );
    const settingsLoaded = persistedSerializedConfig !== null;
    const isDirty =
      settingsLoaded && currentSerializedConfig !== persistedSerializedConfig;
    useUnsavedChangesGuard(isDirty);

    const handleSave = useCallback(() => {
      update(appId, { onboardingConfig: currentSerializedConfig });
    }, [appId, currentSerializedConfig, update]);

    if (schemaError) {
      return <Alert severity='error'>{schemaError.toString()}</Alert>;
    }

    if (schemaLoading || !preparedSchema) {
      return (
        <Box
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
          aria-label='Loading app settings schema'
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <AppSettingsSchemaProvider value={preparedSchema}>
        <Box sx={{ width: "100%" }}>
        <PageTabs tabs={tabList} value={activeTab} onChange={handleChange} />
        <Box sx={{ mt: 2 }}>
          {dataError && <Alert severity='error'>{dataError.toString()}</Alert>}
          {activeTab === 0 && (
            <GeneralTab
              appId={appId}
              publicKey={publicKey}
              config={config}
              onConfigChange={onConfigChange}
            />
          )}
          {activeTab === 1 && (
            <QuestionnaireTab appId={appId} config={config} onConfigChange={onConfigChange} />
          )}
          {activeTab === 2 && (
            <TemplatesTab
              appId={appId}
              config={config}
              onConfigChange={onConfigChange}
            />
          )}
          {activeTab === 3 && (
            <AdvancedTab
              appId={appId}
              config={config}
              onConfigChange={onConfigChange}
            />
          )}
        </Box>
        <Box sx={{ height: 88 }} />
        <Box
          data-testid='app-settings-save-surface'
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            py: 2,
            pl: "160px",
            pr: "60px",
            backgroundColor: "rgba(229, 229, 229, 0.88)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            "@media screen and (max-width: 850px)": {
              pl: "20px",
              pr: "20px",
            },
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center'>
            <Button
              variant='contained'
              onClick={handleSave}
              disabled={!settingsLoaded || !canSave || !isDirty || updateLoading}
            >
              Save
            </Button>
            {isDirty && (
              <Typography color='text.secondary'>
                Don't forget to save changes
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {updateError && (
              <Alert severity='error'>{updateError.toString()}</Alert>
            )}
          </Stack>
        </Box>
        </Box>
      </AppSettingsSchemaProvider>
    );
  },
);
